import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ListChecks, Pencil } from 'lucide-react';
import { departmentLabelKey } from '@nbh/shared';
import { adminApi, type AdminChecklistTemplate } from '../../lib/adminApi';
import { ChecklistTemplateBuilder } from './ChecklistTemplateBuilder';

/** SOP checklist templates (SPEC.md §5.4). Feed the PPM scheduler. */
export function ChecklistTemplateManager() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<AdminChecklistTemplate[] | null>(null);
  const [editing, setEditing] = useState<AdminChecklistTemplate | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    adminApi.checklists
      .list()
      .then(setTemplates)
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(load, [load]);

  if (error && !templates)
    return (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    );
  if (!templates) return <p className="text-sm text-slate-400">{t('admin.loading')}</p>;

  if (editing) {
    return (
      <ChecklistTemplateBuilder
        template={editing === 'new' ? undefined : editing}
        onCancel={() => setEditing(null)}
        onSave={async (body) => {
          if (editing === 'new') await adminApi.checklists.create(body);
          else await adminApi.checklists.update(editing.id, body);
          setEditing(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => setEditing('new')}
        className="min-h-[48px] w-full rounded-xl bg-brookfields-green ring-1 ring-brookfields-gold/70 px-4 text-sm font-semibold text-white"
      >
        {t('adminChecklists.newTemplate')}
      </button>

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {templates.length === 0 ? (
        <p className="text-sm text-slate-500">{t('adminChecklists.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {templates.map((template) => (
            <li key={template.id} className="flex flex-wrap items-center gap-2 p-3">
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate text-sm ${
                    template.isActive ? 'text-slate-100' : 'text-slate-600 line-through'
                  }`}
                >
                  {template.title}
                </span>
                <span className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                  <span>{t(departmentLabelKey[template.department as never])}</span>
                  <span className="inline-flex items-center gap-1">
                    <ListChecks className="size-3" aria-hidden="true" />
                    {t('adminChecklists.stepCount', { count: template.items.length })}
                  </span>
                  {template.items.some((item) => item.requirePhoto) && (
                    <span className="inline-flex items-center gap-1 text-amber-400">
                      <Camera className="size-3" aria-hidden="true" />
                      {t('adminChecklists.hasPhotoSteps')}
                    </span>
                  )}
                  {/* In-use templates are riskier to change, so usage is shown up front. */}
                  {template._count.schedules > 0 && (
                    <span className="text-sky-400">
                      {t('adminChecklists.inUse', { count: template._count.schedules })}
                    </span>
                  )}
                </span>
              </span>

              <button
                type="button"
                onClick={() => setEditing(template)}
                className="inline-flex min-h-[40px] items-center gap-1 rounded bg-slate-800 px-3 text-xs text-slate-200"
              >
                <Pencil className="size-3.5" aria-hidden="true" />
                {t('adminChecklists.edit')}
              </button>

              {template.isActive && (
                <button
                  type="button"
                  onClick={() => {
                    adminApi.checklists
                      .retire(template.id)
                      .then(load)
                      .catch((c: Error) => setError(c.message));
                  }}
                  className="min-h-[40px] rounded bg-transparent px-3 text-xs text-slate-500"
                >
                  {t('adminChecklists.retire')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
